import { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { compact, isEmpty, noop, omit } from 'lodash';
import queryString from 'query-string';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { Icon, SearchOption } from '@app/components';
import { useSearchContext } from '@app/contexts';
import {
  generateRouteParams,
  getDateRangeLabel,
  getShouldMapFacetLabel,
} from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Route, URLQuery, VoidFn } from '@app/constants';

import { FacetInstanceDto } from '../constants';

import './ActiveFacetAttributes.scss';

interface ActiveFacetAttributeValue {
  label?: string | ReactElement;
  value?: string;
  facetKey?: string;
  remove: VoidFn;
}

interface ActiveFacetAttributes {
  facets: Partial<FacetInstanceDto>;
  onFacetAttributeRemoval: (facetKey: string, value: string) => void;
}

const cnBem = createBemBlockBuilder(['activeFacetAttributes']);

export const ActiveFacetAttributes: FC<ActiveFacetAttributes> = ({
  facets,
  onFacetAttributeRemoval,
}) => {
  const { $t } = useIntl();
  const {
    state: { query },
    searchOption,
    activeFacets,
    activePublicationYear,
    setActivePublicationYear,
    updateState,
  } = useSearchContext();
  const [activeFacetAttributes, setActiveFacetAttributes] = useState<
    ActiveFacetAttributeValue[]
  >([]);
  const navigate = useNavigate();
  const location = useLocation();

  const activeFiltersMessage = $t({
    id: 'filterPane.activeFilters',
    defaultMessage: 'Active filters',
  });

  const getFacetAttributeLabel = useCallback(
    (facetKey, facet) =>
      getShouldMapFacetLabel(facetKey)
        ? facets[facetKey].values?.find(({ value }) => value === facet)?.label
        : facet,
    [facets]
  );

  const getActiveFacetAttributes = useCallback(
    () =>
      compact([
        ...Object.keys(activeFacets).flatMap(facetKey => {
          if (!isEmpty(activeFacets[facetKey])) {
            const facetAttributes = activeFacets[facetKey];

            return Object.keys(facetAttributes)
              .filter(facetAttribute => facetAttributes[facetAttribute])
              .map(facetAttribute => ({
                value: facetAttribute,
                label: getFacetAttributeLabel(facetKey, facetAttribute),
                facetKey,
                remove: () => onFacetAttributeRemoval(facetKey, facetAttribute),
              }));
          }

          return [];
        }),
        !isEmpty(activePublicationYear) && {
          label: getDateRangeLabel(activePublicationYear),
          remove: () => setActivePublicationYear({}),
        },
      ]),
    [
      getFacetAttributeLabel,
      onFacetAttributeRemoval,
      activeFacets,
      activePublicationYear,
      setActivePublicationYear,
    ]
  );

  useEffect(() => {
    setActiveFacetAttributes(getActiveFacetAttributes());
  }, [getActiveFacetAttributes, activeFacets, activePublicationYear]);

  const handleClearButtonClick = () => {
    const pathsToOmit = !query
      ? [
          searchOption === SearchOption.keyword ? 'option' : '',
          'pageNumber',
          'recordsPerPage',
        ]
      : [];
    const updatedQuery = omit(queryString.parse(location.search), [
      'facets',
      'publicationYear',
      ...pathsToOmit,
    ]);

    updateState({ isInnerUpdate: true });
    navigate(
      generateRouteParams({
        pathname: Route.search,
        query: updatedQuery as URLQuery,
      })
    );
  };

  return (
    <div className={cnBem()}>
      <div className={cnBem('__header')}>
        {activeFiltersMessage}
        <Button
          styleType="flat-icon"
          className={cnBem('__clearButton')}
          onClick={handleClearButtonClick}
        >
          {$t(sharedMessages.clearAll)}
        </Button>
      </div>
      <ul
        className={cnBem('__facetAttributes')}
        aria-label={activeFiltersMessage}
      >
        {activeFacetAttributes.map((facetAttribute, index) => (
          <li
            key={index}
            {...(!facetAttribute.label && {
              className: cnBem('__facetAttributeSpinner'),
              'data-auto': 'active-facet-attribute-spinner',
            })}
          >
            <Button
              styleType="flat-icon"
              onClick={facetAttribute.remove}
              {...(!facetAttribute.label && {
                'aria-label': $t(sharedMessages.loadingMessage),
                onClick: noop,
              })}
            >
              {facetAttribute?.label && (
                <>
                  {facetAttribute.label} <Icon icon={faTimes} />
                </>
              )}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
